import { memo, PropsWithChildren, ReactNode } from 'react';
import { Button } from 'src/common/Button/Button';
import { FilterIcon } from 'src/modules/common/icons/FilterIcon';
import { Pagination } from 'src/modules/common/components/Pagination/Pagination';
import { Tooltip } from 'src/modules/common/components/Tooltip/Tooltip';
import { CircleFilledIcon } from 'src/modules/common/icons/CircleFilledIcon';
import { OverflowMenu } from 'src/modules/common/components/OverflowMenu/OverflowMenu.tsx';
import { SortIcon } from 'src/modules/common/icons/SortIcon.tsx';
import { OverflowMenuItem } from 'src/modules/common/components/OverflowMenu/OverflowMenuItem.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { ProductAttributeId, QueryParamName } from 'src/utils/composeFilter.ts';
import { DirectionEnum, OrderBy, RegionsEnum } from 'src/generated/operations.ts';
import { ArrowDownIcon } from 'src/modules/common/icons/ArrowDownIcon.tsx';
import { ArrowUpIcon } from 'src/modules/common/icons/ArrowUpIcon.tsx';
import { GridViewIcon } from 'src/modules/common/icons/GridViewIcon';
import { ListViewIcon } from 'src/modules/common/icons/ListViewIcon';
import clsx from 'clsx';
import { DeviceWrapperViewType } from 'src/common/types/DeviceViewType';
import { WarningIconUnfilled } from 'src/modules/common/icons/WarningIconUnfilled.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';

export type OrderItem = [QueryParamName, ProductAttributeId, DirectionEnum];
export type OrderItems = OrderItem[];

type Props = PropsWithChildren<{
    isMobile: boolean;
    currentPage: number;
    pageSize: number;
    hasAdditionalFilter: boolean;
    total: number;
    filterHandler?: () => void;
    onPageChange: (page: number) => void;
    onToggleView: () => void;
    view: DeviceWrapperViewType;
    onOrderByChange: (order: OrderBy[]) => void;
    orderItems: OrderItems;
    orderSelected: OrderBy[];
    defaultOrderLabel: string;
    additionalActions?: ReactNode;
    currentRegion?: RegionsEnum;
}>

const getDirection = (condition: boolean, defaultDirection: DirectionEnum): DirectionEnum => {
    const down = defaultDirection === DirectionEnum.Asc ? DirectionEnum.Asc : DirectionEnum.Desc;
    const up = defaultDirection === DirectionEnum.Asc ? DirectionEnum.Desc : DirectionEnum.Asc;
    return condition ? down : up;
};

export const DeviceGrid = memo(({
    isMobile,
    hasAdditionalFilter,
    currentPage,
    pageSize,
    total,
    filterHandler,
    onPageChange,
    onToggleView,
    view,
    onOrderByChange,
    orderItems = [],
    orderSelected,
    defaultOrderLabel,
    additionalActions,
    currentRegion,
    children,
}: Props) => {
    const shownItemsFrom = ((currentPage - 1) * pageSize) + 1;
    const shownItemsTo = currentPage * pageSize > total ? total : currentPage * pageSize;

    return (
        <div className="vss-device-grid">
            <div className="vss-device-grid__header">
                <p className="vss-device-grid__counter">{shownItemsFrom}-{shownItemsTo} ({total})</p>

                {currentRegion === RegionsEnum.Am && total > 10 && (
                    <div className="vss-device-grid__warning">
                        <Tooltip
                            title={<I18n messageId={MESSAGES.NarrowYourSelectionTitle}/>}
                            value={<I18n messageId={MESSAGES.NarrowYourSelectionDescription}/>}
                        >
                            <WarningIconUnfilled/>
                            {isMobile ? null : <I18n messageId={MESSAGES.NarrowYourSelectionTitle}/>}
                        </Tooltip>
                    </div>
                )}

                <div className="vss-device-grid__actions">
                    {orderItems.length > 0 && isMobile && filterHandler && (
                        <Button
                            kind="ghost-secondary"
                            size="md"
                            onClick={filterHandler}
                        >
                            <div className="vss-device-grid__action-icon">
                                <FilterIcon/>
                            </div>

                            {hasAdditionalFilter && (
                                <div className="vss-device-grid__indicator">
                                    <CircleFilledIcon/>
                                </div>
                            )}
                        </Button>
                    )}

                    {!isMobile && (
                        <>
                            <Button
                                kind="ghost-secondary"
                                iconOnly={true}
                                icon={(
                                    <div className="vss-device-grid__action-icon">
                                        <GridViewIcon/>
                                    </div>
                                )}
                                onClick={onToggleView}
                                disabled={view === 'grid'}
                                aria-pressed={view === 'grid'}
                                data-testid="vss-grid-view-button"
                                className={clsx({
                                    'vss-device-grid__action': true,
                                    'vss-device-grid__action--selected': view === 'grid',
                                })}
                            />

                            <Button
                                kind="ghost-secondary"
                                iconOnly={true}
                                icon={(
                                    <div className="vss-device-grid__action-icon">
                                        <ListViewIcon/>
                                    </div>
                                )}
                                onClick={onToggleView}
                                disabled={view === 'list'}
                                aria-pressed={view === 'list'}
                                data-testid="vss-list-view-button"
                                className={clsx({
                                    'vss-device-grid__action': true,
                                    'vss-device-grid__action--selected': view === 'list',
                                })}
                            />
                        </>
                    )}

                    {orderItems.length > 0 && (
                        <OverflowMenu
                            size="md"
                            kind="ghost-secondary"
                            icon={(
                                <>
                                    <SortIcon/>

                                    {orderSelected.length > 0 && (
                                        <div className="vss-device-grid__indicator">
                                            <CircleFilledIcon/>
                                        </div>
                                    )}
                                </>
                            )}
                        >
                            <OverflowMenuItem
                                selected={orderSelected.length === 0}
                                onClick={() => onOrderByChange([])}
                            >
                                <I18n messageId={defaultOrderLabel}/>

                                {orderSelected.length === 0 && (
                                    <div className="vss-device-grid__action-icon">
                                        <ArrowDownIcon/>
                                    </div>
                                )}
                            </OverflowMenuItem>
                            {orderItems.map(([label, id, defaultDirection]) => {
                                const selected = orderSelected.length > 0 &&
                                    orderSelected.filter((item) => ![id].includes(item.id)).length === 0;
                                let direction = defaultDirection;
                                if (orderSelected.length > 0 && selected) {
                                    direction = orderSelected[0].direction === DirectionEnum.Asc
                                        ? DirectionEnum.Desc : DirectionEnum.Asc;
                                }
                                return (
                                    <OverflowMenuItem
                                        key={label}
                                        selected={selected}
                                        onClick={() => onOrderByChange(
                                            [id].map((attrId) => ({ id: attrId, direction })),
                                        )}
                                    >
                                        <I18n messageId={label}/>
                                        {selected && (
                                            <div className="vss-device-grid__action-icon">
                                                {(direction === getDirection(true, defaultDirection)) ? (
                                                    <ArrowUpIcon/>
                                                ) : (
                                                    <ArrowDownIcon/>
                                                )}
                                            </div>
                                        )}
                                    </OverflowMenuItem>
                                );
                            })}
                        </OverflowMenu>
                    )}

                    {additionalActions}

                </div>
            </div>

            <div className="vss-device-grid__items">
                {children}
            </div>

            {total > pageSize && (
                <Pagination
                    isMobile={isMobile}
                    currentPage={currentPage}
                    total={total}
                    pageSize={pageSize}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
});
